"use client"

import { useCallback, useEffect, useState } from "react"
import { Button, DialogClose, DialogDescription, DialogTitle } from "@ui/shad"
import { Loader2, X } from "lucide-react"

import { useCreateUpgradeAgreement } from "@/hooks/api/onboarding"
import { CustomDialog } from "@/components/dialogs/custom-dialog"

import { PlanButton } from "./plan-button"

type UpgradePlan = "year"
type UpgradeStep = "plan" | "redirecting"

interface UpgradeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UpgradeDialog({ open, onOpenChange }: UpgradeDialogProps) {
  const { mutateAsync: createUpgradeAgreement, isPending } = useCreateUpgradeAgreement()

  const [step, setStep] = useState<UpgradeStep>("plan")
  const [loadingPlan, setLoadingPlan] = useState<UpgradePlan | null>(null)

  useEffect(() => {
    if (open) return

    setStep("plan")
    setLoadingPlan(null)
  }, [open])

  const currentReturnLocation = useCallback(() => {
    const currentUrl = new URL(window.location.href)
    const [bookId, ...returnSegments] = currentUrl.pathname.split("/").filter(Boolean)

    return {
      bookId,
      returnTo: returnSegments.join("/") || "dashboard",
    }
  }, [])

  async function handleSelectPlan(plan: UpgradePlan) {
    if (isPending) return

    setLoadingPlan(plan)
    setStep("redirecting")

    try {
      const { bookId, returnTo } = currentReturnLocation()
      const { checkout_url, signing_url } = await createUpgradeAgreement({
        plan_interval: plan,
        book_id: bookId,
        redirect_book: bookId,
        return_to: returnTo,
      })
      const redirectUrl = signing_url ?? checkout_url
      if (!redirectUrl) throw new Error("No upgrade redirect URL returned")

      window.location.href = redirectUrl
    } catch {
      setLoadingPlan(null)
      setStep("plan")
    }
  }

  return (
    <CustomDialog
      open={open}
      onOpenChange={onOpenChange}
      title={<>Upgrade Slant</>}
      titleProvided
      customClassName={"max-w-xl overflow-hidden p-0"}
      contentProps={{}}
    >
      <div className="relative p-8 sm:p-10">
        <DialogClose asChild>
          <Button variant="ghost" size="iconXs" className="absolute top-3 right-3">
            <X className="text-icon" />
            <span className="sr-only">Close</span>
          </Button>
        </DialogClose>
        {step === "redirecting" ? (
          <LoadingState label="Opening agreement" />
        ) : (
          <>
            <div className="mx-auto max-w-md space-y-2 text-center">
              <DialogTitle className="text-4xl font-semibold">Upgrade Slant</DialogTitle>
              <DialogDescription className="text-base">
                Upgrade to maintain access beyond your trial period and gain full functionality. Have questions? Ask
                your CSM.
              </DialogDescription>
            </div>
            <div className="mx-auto mt-8 max-w-sm">
              <PlanButton
                title="Annual"
                price="$1,800"
                period="/user/year"
                description="Annual subscription, billed once per year."
                className="h-full"
                variant="card"
                loading={loadingPlan === "year"}
                disabled={isPending}
                onClick={() => void handleSelectPlan("year")}
              />
            </div>
          </>
        )}
      </div>
    </CustomDialog>
  )
}

function LoadingState({ label }: { label: string }) {
  return (
    <div className="flex h-full min-h-40 items-center justify-center gap-2 p-6">
      <Loader2 className="text-muted-foreground size-4 animate-spin" />
      <p className="text-muted-foreground text-sm">{label}</p>
    </div>
  )
}
